<template>
  <el-card shadow="hover">
    <div slot="header" class="clearfix">
      <span>
        Source Data
        <small>
          <el-tooltip placement="right" effect="light">
            <span slot="content"
              >Choose the source data for this task. Planning Analytics source
              options include<br />data within a single cube or data across all
              cubes that share the dimension(s) you<br />select. You also have
              the option to send a fixed value to the target location.</span
            >
            <i
              class="el-icon-info"
              style="position: relative; left: 2px; bottom: 4px"
            />
          </el-tooltip>
        </small>
      </span>
    </div>
    <el-form label-position="top">
      <el-form-item label="Source Type" v-if="type !== 'Zero Out'">
        <el-select
          filterable
          v-model="sourceType"
          @change="sourceTypeSelected"
          style="width: 100%"
          :disabled="envRights === 'Run'"
        >
          <el-option
            v-for="item in typeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
            :disabled="item.disabled"
          />
        </el-select>
      </el-form-item>
      <el-form-item v-if="sourceType !== 'Value'">
        <span slot="label">
          Multiply Factor
          <small>
            <el-tooltip placement="right" effect="light">
              <span slot="content"
                >Use the multiply factor to multiply all values in your source
                data<br />set by a single number. For example, a factor of 1.2
                would increase<br />all values in the target location by
                20%.</span
              >
              <i
                class="el-icon-info"
                style="position: relative; left: 2px; bottom: 4px"
              />
            </el-tooltip>
          </small>
        </span>
        <el-input
          @blur="factorUpdated"
          v-model="factor"
          type="number"
          :class="{ error: checkValid && $v.factor.$invalid }"
          :disabled="envRights === 'Run'"
        />
      </el-form-item>

      <el-form-item
        label="Cube"
        v-if="sourceType === 'Single Cube' && type !== 'Zero Out'"
      >
        <el-select
          filterable
          v-model="sourceCube"
          @change="getCubeDims"
          placeholder
          style="width: 100%"
          :class="{ error: checkValid && $v.sourceCube.$invalid }"
          :disabled="envRights === 'Run'"
        >
          <el-option
            v-for="item in cubeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
            :disabled="item.disabled"
          />
        </el-select>
      </el-form-item>
    </el-form>
    <el-form
      label-position="top"
      v-if="sourceType === 'Single Cube' && type !== 'Zero Out' && sourceCube"
    >
      <el-form-item
        v-for="({ name }, index) in sourceCubeDims"
        :key="index"
        :label="name"
      >
        <b-input-group :prepend="sourceCubeDims[index].selectionType" size="sm">
          <b-form-input
            :disabled="envRights === 'Run'"
            v-model="$v.sourceCubeDims.$model[index].display"
            :class="{
              error: checkValid && $v.sourceCubeDims.$each[index].$invalid,
            }"
            type="text"
            size="sm"
            readonly
            :style="{
              backgroundColor: envRights !== 'Run' && '#fff',
              cursor: envRights !== 'Run' && 'pointer',
            }"
            @click="dimSelected(sourceCubeDims[index], index, 'sourceCube')"
          />
          <b-input-group-append>
            <b-button
              :disabled="envRights === 'Run'"
              size="sm"
              @click="clearSelection('sourceCube', index)"
            >
              <b-icon icon="x" />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </el-form-item>
    </el-form>
    <el-collapse
      v-model="collapseItems"
      style="margin-top: 10px"
      v-if="sourceType === 'Multiple Cubes'"
    >
      <el-collapse-item name="2">
        <template slot="title">
          Cubes ({{ filteredCubes.length }} Available,
          {{ cubesSelected }} Selected)
        </template>
        <el-checkbox
          v-model="selectAllCheckbox"
          @change="selectAll"
          style="font-weight: 400"
          :disabled="filteredCubes.length === 0 || envRights === 'Run'"
        >
          Select All
        </el-checkbox>
        <el-checkbox-group v-model="sourceCubeList">
          <el-checkbox
            v-for="item in filteredCubes"
            :label="item"
            :key="item"
            style="display: block"
            @change="cubeListUpdated"
            :disabled="envRights === 'Run'"
            >{{ item }}
          </el-checkbox>
        </el-checkbox-group>
      </el-collapse-item>
      <el-collapse-item name="1">
        <template slot="title">
          Dimensions ({{ sourceDims.length }} Selected)
        </template>
        <el-button
          class="button"
          @click="addDim"
          :disabled="envRights === 'Run'"
          icon="el-icon-plus"
          >Add Dimension</el-button
        >
        <el-form label-position="top">
          <div class="border" v-for="index in sourceDims.length" :key="index">
            <el-form-item label="Dimension" style="margin: 0 10px 10px 10px">
              <b-input-group>
                <b-form-select
                  :disabled="envRights === 'Run'"
                  v-model="$v.sourceDims.$model[index - 1].name"
                  :class="{
                    error:
                      checkValid &&
                      $v.sourceDims.$each[index - 1].name.$invalid,
                  }"
                  size="sm"
                  :options="dims"
                  @change="dimAdded(index - 1)"
                />
                <b-input-group-append>
                  <b-button
                    :disabled="envRights === 'Run'"
                    size="sm"
                    @click="removeDim(index - 1)"
                  >
                    <b-icon icon="trash" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </el-form-item>
            <el-form-item label="Element" style="margin: 0 10px 10px 10px">
              <b-input-group
                :prepend="sourceDims[index - 1].selectionType"
                size="sm"
              >
                <b-form-input
                  :disabled="!sourceDims[index - 1].name || envRights === 'Run'"
                  v-model="$v.sourceDims.$model[index - 1].display"
                  :class="{
                    error:
                      checkValid && $v.sourceDims.$each[index - 1].$invalid,
                  }"
                  type="text"
                  size="sm"
                  readonly
                  :style="{
                    backgroundColor:
                      sourceDims[index - 1].name &&
                      envRights !== 'Run' &&
                      '#fff',
                    cursor:
                      sourceDims[index - 1].name &&
                      envRights !== 'Run' &&
                      'pointer',
                  }"
                  @click="
                    dimSelected(sourceDims[index - 1], index - 1, 'source')
                  "
                />
                <b-input-group-append>
                  <b-button
                    :disabled="envRights === 'Run'"
                    size="sm"
                    @click="clearSelection('source', index - 1)"
                  >
                    <b-icon icon="x" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </el-form-item>
          </div>
        </el-form>
      </el-collapse-item>
    </el-collapse>
    <el-form
      v-if="sourceType === 'Value' && type !== 'Zero Out'"
      label="Value"
      style="margin-top: 10px"
    >
      <el-input
        type="number"
        style="width: 100%"
        v-model="sourceValue"
        @blur="sourceValueUpdated"
        :disabled="envRights === 'Run'"
        :class="{ error: checkValid && $v.sourceValue.$invalid }"
      />
    </el-form>
  </el-card>
</template>

<script>
import { required, requiredIf } from "vuelidate/lib/validators";
import { mapState } from "vuex";

export default {
  props: {
    taskType: String,
    taskSourceType: String,
    taskFactor: [Number, String],
    taskSourceCube: String,
    taskSourceCubeDims: Array,
    taskSourceDims: Array,
    taskSourceValue: [Number, String],
    taskTargetCubeDims: Array,
    taskCubes: Array,
    taskDims: Array,
    taskCheckValid: Boolean,
    taskFilteredCubes: Array,
    taskSourceCubeList: Array,
    taskSelectAll: Boolean,
  },
  computed: {
    typeOptions() {
      const options = [
        {
          value: "Single Cube",
          label: "Single Cube",
        },
        {
          value: "Multiple Cubes",
          label: "Multiple Cubes",
        },
        {
          value: "Value",
          label: "Value",
          disabled: this.type === "Move",
        },
      ];
      return options;
    },
    cubeOptions() {
      const options = this.taskCubes.map((name) => {
        return { value: name, label: name };
      });
      return options;
    },
    dimOptions() {
      const options = this.taskDims.map((name) => {
        return { value: name, label: name };
      });
      return options;
    },
    cubesSelected() {
      return Math.min(this.sourceCubeList.length, this.filteredCubes.length);
    },
    ...mapState(["envRights"]),
  },
  data() {
    return {
      type: this.taskType,
      sourceType: this.taskSourceType,
      factor: this.taskFactor,
      sourceCube: this.taskSourceCube,
      sourceCubeDims: this.taskSourceCubeDims,
      sourceDims: this.taskSourceDims,
      sourceValue: this.taskSourceValue,
      targetCubeDims: this.taskTargetCubeDims,
      cubes: this.taskCubes,
      dims: this.taskDims,
      checkValid: false,
      sourceCubeList: this.taskSourceCubeList,
      filteredCubes: this.taskCubes,
      collapseItems: [1, "1"],
      selectAllCheckbox: false,
    };
  },
  watch: {
    taskSelectAll(value) {
      this.selectAllCheckbox = value;
    },
    taskSourceCubeList(value) {
      this.sourceCubeList = value;
    },
    taskType(value) {
      this.type = value;
      if (value === "Move" && this.sourceType === "Value") {
        this.sourceType = "Single Cube";
        this.sourceTypeSelected("Single Cube");
      } else if (value === "Zero Out") {
        this.sourceValue = 0;
      }
    },
    taskSourceType(value) {
      this.sourceType = value;
    },
    taskFactor(value) {
      this.factor = value === null ? 1 : value;
    },
    taskSourceCube(value) {
      this.sourceCube = value;
    },
    taskSourceCubeDims(value) {
      this.sourceCubeDims = value;
    },
    taskSourceDims(value) {
      this.sourceDims = value;
    },
    taskSourceValue(value) {
      this.sourceValue = value;
    },
    taskTargetCubeDims(value) {
      this.targetCubeDims = value;
    },
    taskCubes(value) {
      this.cubes = value;
    },
    taskDims(value) {
      this.dims = value;
    },
    taskCheckValid(value) {
      this.checkValid = value;
    },
    taskFilteredCubes(value) {
      this.filteredCubes = value;
      this.sourceCubeList = this.sourceCubeList.filter((cube) =>
        this.filteredCubes.includes(cube)
      );
      if (this.filteredCubes.length === 0) {
        this.selectAllCheckbox = false;
        this.$emit("selectAllUpdated", this.selectAllCheckbox);
      }
      this.$emit("cubeListUpdated", this.sourceCubeList);
    },
  },
  methods: {
    formatForDisplay(value) {
      if (value && value[0] === "[" && value[value.length - 1] === "]") {
        value = JSON.parse(value);
        return `${value.length} Elements`;
      }
      return value;
    },
    /**
     * Updates the interface after modifying the list of cubes
     * @function
     */
    cubeListUpdated() {
      this.selectAllCheckbox =
        JSON.stringify([...this.sourceCubeList].sort()) ===
        JSON.stringify([...this.filteredCubes].sort());
      this.$emit("selectAllUpdated", this.selectAllCheckbox);
      this.$emit("cubeListUpdated", this.sourceCubeList);
    },
    /**
     * Updates the interface after modifying the select all cubes option
     * @function
     */
    selectAll() {
      this.sourceCubeList = this.selectAllCheckbox ? this.filteredCubes : [];
      this.$emit("cubeListUpdated", this.sourceCubeList);
      this.$emit("selectAllUpdated", this.selectAllCheckbox);
    },
    /**
     * Searches for the dimension name
     * @function
     */
    querySearch(queryString, cb) {
      const result = queryString
        ? this.dims.filter((dim) =>
            dim.value.toLowerCase().includes(queryString)
          )
        : this.dims;
      cb(result);
    },
    /**
     * Updates the interface after modifying the task source value
     * @function
     */
    sourceValueUpdated() {
      this.sourceValue = Number(this.sourceValue);
      this.$emit("sourceValueUpdated", this.sourceValue);
    },
    /**
     * Updates the interface after modifying the task multiplication factor
     * @function
     */
    factorUpdated() {
      this.factor = Number(this.factor);
      if (this.factor === 0) this.factor = 1;
      this.$emit("factorUpdated", this.factor);
    },
    /**
     * Clears the dimension element selection
     * @function
     * @param {String} type - Type of the dimension
     * @param {Number} index - Index of the dimension
     */
    clearSelection(type, index) {
      this.$emit("selectionSubmitted", {
        type,
        index,
        selection: null,
        selectionType: null,
        selectionAlias: null,
      });
    },
    /**
     * Updates the dimension selection and triggers the modal
     * @function
     * @param {String} dim - Name of the dimension
     * @param {Number} index - Index of the dimension
     * @param {String} type - Type of the dimension
     */
    dimSelected(dim, index, type) {
      this.$emit("dimSelected", {
        dim,
        index,
        type,
      });
    },
    /**
     * Updates the interface after selecting a task source type
     * @function
     * @param {String} type - Type of the task source
     */
    sourceTypeSelected(type) {
      this.$emit("sourceTypeSelected", type);
    },
    /**
     * Fetches the list of dimensions associated with a particular cube
     * @function
     */
    getCubeDims() {
      this.$emit("getCubeDims", {
        card: "source",
        cube: this.sourceCube,
      });
    },
    /**
     * Adds a dimension to the task source dimensions
     * @function
     */
    addDim() {
      this.$emit("addDim");
    },
    /**
     * Removes a dimension from the task source dimensions
     * @function
     */
    removeDim(index) {
      this.$emit("removeDim", index);
    },
    /**
     * Filters the cube list and fetches the list of elements associated with a particular dimension
     * @function
     * @param {Number} index - Index of the task source dimension
     */
    dimAdded(index) {
      this.$emit("dimAdded", index);
    },
  },
  validations() {
    return {
      sourceType: { required },
      factor: {
        required,
      },
      sourceCube: {
        required: requiredIf(() => {
          return this.sourceType === "Single Cube";
        }),
      },
      sourceCubeDims: {
        $each: {
          selection: {
            required: requiredIf((value) => {
              if (this.sourceType === "Single Cube") {
                const targetDim = this.targetCubeDims.find(
                  (dim) => value.name === dim.name
                );
                return targetDim ? targetDim.selection : false;
              } else {
                return false;
              }
            }),
          },
        },
      },
      sourceDims: {
        $each: {
          name: {
            required: requiredIf(() => {
              return this.sourceType === "Multiple Cubes";
            }),
            isUnique(value) {
              const results = this.sourceDims.filter(
                (dim) => dim.name === value
              );
              return results.length === 1;
            },
          },
          selection: {
            required: requiredIf(() => {
              return this.sourceType === "Multiple Cubes";
            }),
          },
        },
      },
      sourceValue: {
        required: requiredIf(() => {
          return this.sourceType === "Value";
        }),
        notZero(value) {
          return this.type !== "Zero Out" && this.sourceType === "Value"
            ? value !== 0
            : true;
        },
      },
    };
  },
};
</script>

<style scoped>
.error :deep(.el-input__inner) {
  border: 1px solid red;
  border-radius: 4px;
}
/* Delete when BootstrapVue objects removed */
.form-control.form-control-sm.error {
  border: 1px solid red;
  border-radius: 4px;
}
/* Delete when BootstrapVue objects removed */
.custom-select.custom-select-sm.error {
  border: 1px solid red;
  border-radius: 4px;
}
.button {
  background-color: #b3282d;
  color: white;
  border-color: white;
  padding: 10px 15px;
}
.button:focus {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.button:hover {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.border {
  margin-top: 20px;
  padding-bottom: 8px;
  border: 1px #dcdfe6 solid !important;
  border-radius: 4px;
}
</style>
